// ─── Branded Dialog ─────────────────────────────────────────────────
// Drop-in replacement for window.confirm() and window.alert().
// Uses a React context so any component can call:
//   const { confirm, alert } = useBrandedDialog();
//   const ok = await confirm('Dismiss all failed sync entries?');
//   await alert('Cache purge triggered.');

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/* ─── Types ────────────────────────────────────────────────────────── */
interface DialogOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
}

interface DialogContextValue {
  confirm: (messageOrOpts: string | DialogOptions) => Promise<boolean>;
  alert: (messageOrOpts: string | DialogOptions) => Promise<void>;
}

/* ─── Context ──────────────────────────────────────────────────────── */
const DialogContext = createContext<DialogContextValue | null>(null);

export function useBrandedDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useBrandedDialog must be used within <BrandedDialogProvider>');
  return ctx;
}

/* ─── Provider ─────────────────────────────────────────────────────── */
interface InternalState {
  open: boolean;
  mode: 'confirm' | 'alert';
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  variant: 'default' | 'danger';
}

export function BrandedDialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<InternalState>({
    open: false,
    mode: 'confirm',
    title: '',
    message: '',
    confirmLabel: 'OK',
    cancelLabel: 'Cancel',
    variant: 'default',
  });

  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const openDialog = useCallback((mode: 'confirm' | 'alert', opts: DialogOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      resolveRef.current = resolve;
      setState({
        open: true,
        mode,
        title: opts.title || (mode === 'confirm' ? 'Confirm' : 'Notice'),
        message: opts.message,
        confirmLabel: opts.confirmLabel || 'OK',
        cancelLabel: opts.cancelLabel || 'Cancel',
        variant: opts.variant || 'default',
      });
    });
  }, []);

  const confirm = useCallback((messageOrOpts: string | DialogOptions): Promise<boolean> => {
    const opts = typeof messageOrOpts === 'string' ? { message: messageOrOpts } : messageOrOpts;
    return openDialog('confirm', opts);
  }, [openDialog]);

  const alert = useCallback((messageOrOpts: string | DialogOptions): Promise<void> => {
    const opts = typeof messageOrOpts === 'string' ? { message: messageOrOpts } : messageOrOpts;
    return openDialog('alert', opts).then(() => {});
  }, [openDialog]);

  const handleClose = useCallback((result: boolean) => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.(result);
    resolveRef.current = null;
    const trigger = triggerRef.current;
    triggerRef.current = null;
    queueMicrotask(() => trigger?.focus());
  }, []);

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}
      {state.open && (
        <DialogOverlay
          {...state}
          onConfirm={() => handleClose(true)}
          onCancel={() => handleClose(false)}
        />
      )}
    </DialogContext.Provider>
  );
}

/* ─── Overlay ──────────────────────────────────────────────────────── */
interface OverlayProps extends InternalState {
  onConfirm: () => void;
  onCancel: () => void;
}

function DialogOverlay({ mode, title, message, confirmLabel, cancelLabel, variant, onConfirm, onCancel }: OverlayProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = React.useId();
  const messageId = React.useId();

  React.useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  React.useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? []);
      if (focusable.length === 0) return;

      const activeIndex = focusable.indexOf(document.activeElement as HTMLElement);
      const nextIndex = event.shiftKey
        ? (activeIndex <= 0 ? focusable.length - 1 : activeIndex - 1)
        : (activeIndex === focusable.length - 1 ? 0 : activeIndex + 1);
      event.preventDefault();
      focusable[nextIndex]?.focus();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  return (
    <div style={styles.backdrop} onClick={onCancel}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        style={styles.card}
        onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
      >
        {/* Logo + Title */}
        <div style={styles.header}>
          <div style={styles.logoMark}><svg viewBox="0 0 48 28" fill="none" xmlns="http://www.w3.org/2000/svg" width="28" height="16"><defs><linearGradient id="dlg-grad" x1="0" y1="14" x2="42" y2="14" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#00E5FF"/><stop offset="100%" stopColor="#8B5CF6"/></linearGradient></defs><path d="M13 6C7.5 6 3 9.9 3 14s4.5 8 10 8c3.2 0 6-1.4 8-3.6C23 20.6 25.8 22 29 22c5.5 0 10-3.9 10-8s-4.5-8-10-8c-3.2 0-6 1.4-8 3.6C19 7.4 16.2 6 13 6z" stroke="url(#dlg-grad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
          <span id={titleId} style={styles.title}>{title}</span>
        </div>

        {/* Message */}
        <p id={messageId} style={styles.message}>{message}</p>

        {/* Actions */}
        <div style={styles.actions}>
          {mode === 'confirm' && (
            <button type="button" style={styles.btnCancel} onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            style={{
              ...styles.btnConfirm,
              ...(variant === 'danger' ? styles.btnDanger : {}),
            }}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Styles ───────────────────────────────────────────────────────── */
const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    animation: 'branded-dialog-fade-in 0.15s ease',
  },
  card: {
    background: '#111127',
    border: '1px solid #2A2A45',
    borderRadius: 12,
    padding: '28px 32px',
    maxWidth: 420,
    width: '90vw',
    boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 60px rgba(0,229,255,0.04)',
    animation: 'branded-dialog-scale-in 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
  },
  logoMark: {
    width: 28,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: '#F0F0FA',
    fontFamily: "var(--font-sans)",
    letterSpacing: '-0.01em',
  },
  message: {
    fontSize: 14,
    color: '#C8C8E0',
    fontFamily: "var(--font-mono)",
    lineHeight: 1.6,
    margin: '0 0 24px 0',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  btnCancel: {
    padding: '10px 20px',
    background: 'transparent',
    border: '1px solid #2A2A45',
    borderRadius: 8,
    color: '#C8C8E0',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "var(--font-mono)",
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  btnConfirm: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #00E5FF, #8B5CF6)',
    border: 'none',
    borderRadius: 8,
    color: 'white',
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "var(--font-mono)",
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    letterSpacing: '0.02em',
  },
  btnDanger: {
    background: '#EF4444',
  },
};
